import { useState } from 'react'
import { useAdminUsers, useAuditLog } from '@/hooks/useAdmin'

// Every action the FastAPI backend emits, plus legacy Streamlit actions that
// still live in historical audit data (data_sync / vendor_rules_updated /
// cache_refresh / po_force_deleted). Keep in sync with database.log_action
// call sites — a missing entry just means it can't be filtered, never that it
// won't be recorded (the unfiltered "All actions" view always shows it).
const ACTIONS = [
  '',
  // Auth
  'login_success', 'login_failed', 'logout',
  // Purchase orders
  'po_created', 'po_updated', 'po_deleted', 'po_force_deleted', 'po_merged',
  'po_submitted', 'po_approved', 'po_approved_direct', 'po_rejected',
  'po_marked_ordered', 'po_archived', 'po_received', 'po_exported',
  'po_retry_sync',
  // Users / admin / settings
  'user_created', 'user_updated', 'inventory_manual_adjust', 'settings_changed',
  // Legacy Streamlit actions (historical rows)
  'data_sync', 'vendor_rules_updated', 'cache_refresh',
] as const

export function AuditLogSection() {
  const [username, setUsername] = useState('')
  const [action, setAction] = useState('')
  const { data: users = [] } = useAdminUsers()
  const { data: entries = [], isLoading, isFetching, isError, error, refetch } = useAuditLog({
    username: username || undefined,
    action: action || undefined,
    limit: 100,
  })

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">Audit Log</h2>
      <div className="flex gap-2 items-end">
        <label className="text-xs flex flex-col">
          User
          <select value={username} onChange={(e) => setUsername(e.target.value)} className="px-2 py-1 border border-[var(--line-input)] bg-[var(--surface-card)] focus:outline-none focus:border-[var(--brand)]">
            <option value="">All users</option>
            {users.map((u) => <option key={u.user_id} value={u.username}>{u.username}</option>)}
          </select>
        </label>
        <label className="text-xs flex flex-col">
          Action
          <select value={action} onChange={(e) => setAction(e.target.value)} className="px-2 py-1 border border-[var(--line-input)] bg-[var(--surface-card)] focus:outline-none focus:border-[var(--brand)]">
            {ACTIONS.map((a) => <option key={a} value={a}>{a || 'All actions'}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="px-2 py-1 border border-[var(--line-warm)] bg-[var(--surface-card)] text-xs hover:bg-[var(--surface-page)] disabled:opacity-50"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)] max-h-[480px]">
        <table className="text-xs w-full">
          <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)] sticky top-0">
            <tr className="text-left text-[var(--mute)]">
              <th className="tbl-th w-44">Timestamp</th>
              <th className="tbl-th w-28">User</th>
              <th className="tbl-th w-44">Action</th>
              <th className="tbl-th">Details</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={4} className="px-2 py-4 text-center text-[var(--mute)]">Loading…</td></tr>}
            {!isLoading && isError && (
              <tr><td colSpan={4} className="px-2 py-4 text-center bg-[var(--status-rejected-bg)] text-[var(--status-rejected-fg)]" data-testid="audit-log-error">
                Failed to load audit log: {error instanceof Error ? error.message : 'request failed'}
              </td></tr>
            )}
            {!isLoading && !isError && entries.length === 0 && <tr><td colSpan={4} className="px-2 py-4 text-center text-[var(--mute)]">No entries.</td></tr>}
            {entries.map((e, i) => (
              <tr
                key={e.log_id}
                className={`border-b border-[var(--line-warm)] ${
                  i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                }`}
              >
                <td className="px-2 py-1 tabular-nums text-[var(--ink-2)]">{e.timestamp?.slice(0, 19).replace('T', ' ') ?? '—'}</td>
                <td className="px-2 py-1">{e.username ?? '—'}</td>
                <td className="px-2 py-1">{e.action}</td>
                <td className="px-2 py-1 text-[var(--ink-2)]">{e.details ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
